/**
 * Manual scroll restoration for the hero ScrollTrigger pin.
 *
 * Browser default restoration lands in the pre-GSAP CSS runway; the same
 * `scrollY` maps to a different visual band once the intro pin spacer exists.
 * We hold at 0 until Lenis + the hero scene are ready, then jump and sync.
 *
 * Import this module from the client shell (`SmoothScroll`) so
 * `history.scrollRestoration = 'manual'` runs as early as that island loads.
 */

import { SCROLL_RESTORATION_STORAGE_KEY } from '@/constants/scrollRestoration';
import { getLenisInstance } from '@/lib/scroll';
import { updateScrollTrigger } from '@/motion/scrollScene';

let pendingScrollY: number | null = null;
let smoothScrollReady = false;
let heroSceneReady = false;
let hasRestored = false;
let bootReadyPromise: Promise<void> | null = null;
let resolveBootReady: (() => void) | null = null;

function prefersReducedMotionNow(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readAndClearStoredScrollY(): number | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_RESTORATION_STORAGE_KEY);
    if (raw == null) return null;
    sessionStorage.removeItem(SCROLL_RESTORATION_STORAGE_KEY);
    const y = Number(raw);
    return Number.isFinite(y) && y > 0 ? Math.round(y) : null;
  } catch {
    return null;
  }
}

function writeStoredScrollY(y: number): void {
  try {
    sessionStorage.setItem(SCROLL_RESTORATION_STORAGE_KEY, String(Math.round(y)));
  } catch {
    // Private mode / quota — restoration is best-effort.
  }
}

/** Persist current (or still-pending) scroll Y. Call from `pagehide`. */
export function persistScrollPosition(): void {
  if (typeof window === 'undefined') return;

  // Don't clobber a pending restore with the temporary hold-at-0 position.
  if (!hasRestored && pendingScrollY != null) {
    writeStoredScrollY(pendingScrollY);
    return;
  }

  writeStoredScrollY(window.scrollY);
}

/** Read and clear the in-memory pending Y (sessionStorage already cleared at init). */
export function consumePendingScrollPosition(): number | null {
  const y = pendingScrollY;
  pendingScrollY = null;
  return y;
}

/** Jump to `y` via Lenis when present, otherwise native scroll. */
export function restoreScrollPosition(y: number): void {
  if (typeof window === 'undefined') return;

  const lenis = getLenisInstance();
  if (lenis) {
    lenis.scrollTo(y, { immediate: true });
    return;
  }

  window.scrollTo(0, y);
}

function restoreHashTarget(hashId: string): boolean {
  const element = document.getElementById(hashId);
  if (!element) return false;

  const lenis = getLenisInstance();
  if (lenis) {
    lenis.scrollTo(element, { immediate: true });
  } else {
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  return true;
}

function notifyBootReady(): void {
  resolveBootReady?.();
  resolveBootReady = null;
}

/**
 * Resolves once scroll restoration has run (Lenis + hero GSAP scene gates,
 * or the reduced-motion path). Used by the brand preloader to dismiss only
 * after the page is at the correct scroll band.
 */
export function whenBootReady(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (hasRestored) {
    return Promise.resolve();
  }

  bootReadyPromise ??= new Promise<void>((resolve) => {
    resolveBootReady = resolve;
  });

  return bootReadyPromise;
}

/**
 * Restore once when readiness gates pass. Hash targets win over a saved Y
 * so shared `#section` links stay correct after refresh.
 */
export function tryRestoreScroll(): void {
  if (typeof window === 'undefined' || hasRestored) return;
  if (!smoothScrollReady) return;

  // Motion path waits for the hero pin spacer; reduced motion has no GSAP scene.
  if (!prefersReducedMotionNow() && !heroSceneReady) return;

  hasRestored = true;

  const hashId = window.location.hash.replace(/^#/, '');
  if (hashId && restoreHashTarget(hashId)) {
    pendingScrollY = null;
    updateScrollTrigger();
    notifyBootReady();
    return;
  }

  const y = consumePendingScrollPosition();
  if (y != null) {
    restoreScrollPosition(y);
    updateScrollTrigger();
  }

  notifyBootReady();
}

export function markSmoothScrollReady(): void {
  smoothScrollReady = true;
  tryRestoreScroll();
}

export function markHeroSceneReady(): void {
  heroSceneReady = true;
  tryRestoreScroll();
}

/** Clear the hero gate if the scene effect re-runs before restore completes. */
export function resetHeroSceneReady(): void {
  heroSceneReady = false;
}

// --- Early init (browser only) ------------------------------------------------

if (typeof window !== 'undefined') {
  try {
    history.scrollRestoration = 'manual';
  } catch {
    // Ignore — rare in older environments.
  }

  pendingScrollY = readAndClearStoredScrollY();

  // Kill any browser restore that raced ahead of `manual`, and keep the
  // pre-GSAP runway from painting mid-scene with intro `visibility: hidden`.
  window.scrollTo(0, 0);
}
